// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

/**
 * Exponential Backoff Utility for API Requests
 * Prevents flood of errors during connectivity issues
 */

class ExponentialBackoff {
  constructor(options = {}) {
    this.baseDelay = options.baseDelay || 1000; // Start with 1 second
    this.maxDelay = options.maxDelay || 20000; // Max 20 seconds
    this.multiplier = options.multiplier || 2; // Double each time
    this.jitter = options.jitter !== undefined ? options.jitter : true; // Add randomness
    this.maxRetries = options.maxRetries || 10;

    // Track backoff state per endpoint/key
    this.backoffStates = new Map();

    // Global error throttling
    this.errorThrottleWindow = 5000; // 5 second window
    this.maxErrorsPerWindow = 3; // Max 3 errors per window
    this.errorTimestamps = [];
  }

  /**
   * Get the current backoff delay for a specific key
   */
  getDelay(key) {
    const state = this.backoffStates.get(key) || {
      retryCount: 0,
      lastError: null,
    };

    if (state.retryCount === 0) {
      return 0; // No delay for first attempt
    }

    // Calculate exponential delay
    let delay = Math.min(
      this.baseDelay * Math.pow(this.multiplier, state.retryCount - 1),
      this.maxDelay
    );

    // Add jitter to prevent thundering herd
    if (this.jitter) {
      delay = delay * (0.5 + Math.random() * 0.5);
    }

    return Math.round(delay);
  }

  /**
   * Record a failure and increment retry count
   */
  recordFailure(key, error = null) {
    const state = this.backoffStates.get(key) || {
      retryCount: 0,
      lastError: null,
    };
    state.retryCount = Math.min(state.retryCount + 1, this.maxRetries);
    state.lastError = error;
    state.lastFailureTime = Date.now();
    this.backoffStates.set(key, state);

    // Track error for throttling
    this.trackError();

    return this.getDelay(key);
  }

  /**
   * Reset backoff state for a key (on success)
   */
  recordSuccess(key) {
    const state = this.backoffStates.get(key);
    if (state) {
      // Gradually reduce retry count on success
      if (state.retryCount > 0) {
        state.retryCount = Math.max(0, state.retryCount - 2);
        this.backoffStates.set(key, state);
      } else {
        this.backoffStates.delete(key);
      }
    }
  }

  /**
   * Check if we should throttle errors (to prevent console spam)
   */
  shouldThrottleError() {
    const now = Date.now();

    // Clean old timestamps
    this.errorTimestamps = this.errorTimestamps.filter(
      (timestamp) => now - timestamp < this.errorThrottleWindow
    );

    return this.errorTimestamps.length >= this.maxErrorsPerWindow;
  }

  /**
   * Track an error for throttling purposes
   */
  trackError() {
    const now = Date.now();
    this.errorTimestamps.push(now);

    // Keep only recent timestamps
    this.errorTimestamps = this.errorTimestamps.filter(
      (timestamp) => now - timestamp < this.errorThrottleWindow
    );
  }

  /**
   * Get retry info for debugging
   */
  getRetryInfo(key) {
    const state = this.backoffStates.get(key);
    if (!state) return null;

    return {
      retryCount: state.retryCount,
      nextDelay: this.getDelay(key),
      lastError: state.lastError,
      lastFailureTime: state.lastFailureTime,
    };
  }

  /**
   * Clear all backoff states
   */
  reset() {
    this.backoffStates.clear();
    this.errorTimestamps = [];
  }

  /**
   * Clear backoff state for a specific key
   */
  resetKey(key) {
    this.backoffStates.delete(key);
  }
}

// Singleton instance for global use
const globalBackoff = new ExponentialBackoff();

/**
 * Get the global backoff instance
 */
export function getGlobalBackoff() {
  return globalBackoff;
}
